import { ComponentChildren, ComponentChild } from 'preact';
import { useCallback, useEffect, useRef } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { usePress } from '../hooks/UNSAFE_usePress';
import { useUser } from '../hooks/UNSAFE_useUser';
import { Text } from '../UNSAFE_Text';
import { Flex } from '../UNSAFE_Flex';
import { useTabBarContext } from './useTabBarContext';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TabBarItemVariantOptions } from './themes/TabBarItemStyles.css';
import { TabBarItemRedwoodTheme } from './themes/redwood/TabBarItemTheme';
import { Badge } from '../UNSAFE_Badge';
import { WarningS } from '../UNSAFE_RedwoodIcons/WarningS';
import { ErrorS } from '../UNSAFE_RedwoodIcons/ErrorS';
import { InformationS } from '../UNSAFE_RedwoodIcons/InformationS';
import { SuccessS } from '../UNSAFE_RedwoodIcons/SuccessS';

type Severity = 'warning' | 'info' | 'none' | 'error' | 'confirmation';

export type BaseTabBarItemProps<K extends string | number> = {
  /**
   * Key of the TabBarItem
   */
  itemKey: K;
  /**
   * Label of the TabBarItem. For icon only display this label is the content
   * for aria-label and tooltip text of the Tab.
   */
  label: string;
  /**
   * The icon before the label in non-stack case, icon above the label in
   * stacked case or the stand alone icon when no label is specified.
   */
  icon?: ComponentChild;
  /**
   * The content to be rendered inside the Badge component.
   */
  badge?: string;
  /**
   * The content to be rendered inside the Text component as a metadata,
   * that appears after the label in non stack case only.
   */
  metadata?: string;
  /**
   * The status icon to be rendered after the label in non stack case only.
   */
  severity?: Severity;
  /**
   * The content to be rendered for remove icon
   */
  removeIcon?: ComponentChildren;
  /**
   * The control for enabling and disabling focusRing
   */
  showFocusRingOverride?: boolean;
};

export function BaseTabBarItem<K extends string | number>({
  itemKey,
  label,
  icon,
  badge,
  metadata,
  severity = 'none',
  removeIcon,
  showFocusRingOverride
}: BaseTabBarItemProps<K>) {
  const {
    onSelect,
    selection,
    currentKey,
    showFocusRing,
    layout,
    display,
    size,
    isEdgeBottom,
    tabItemPrefix
  } = useTabBarContext();

  const isCurrent = currentKey === itemKey;

  const ref = useRef<HTMLDivElement>(null);

  /**
   * FIXME: 
   *
   * This is a temporary workaround until [JET-63308]() is resolved.
   *
   * This will optimistically attempt to scroll tab items into view as the tab
   * item becomes "current" regardless of whether this tab item is nested
   * inside of a conveyor/scrollable container.
   *
   * After JET-63308 (or equivalent) is implemented, we can remove this with a
   * proper utility that only invokes inside of a scrolling context.
   */
  useEffect(() => {
    if (ref.current && isCurrent) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [isCurrent]);

  //Selection
  //Dependency on currentKey because upon removal selectionChange does not change hence no re render
  //and it is still holding the deleted key as selection
  const handlePress = useCallback(() => {
    onSelect?.({ value: itemKey as K });
  }, [itemKey, onSelect]);

  const { pressProps } = usePress(handlePress);

  const isSelected = selection === itemKey;

  const isStacked = display === 'stacked' ? true : false;
  const isIconOnly = display === 'icons' ? true : false;
  const isTall = size === 'lg' ? true : false;
  const labelRequirement = label && !(label === ' ' || label === undefined);
  const severityRequirement = !(severity === 'none' || severity === undefined);

  const IconComponent = (severityValue: Severity) => {
    switch (severityValue) {
      case 'error':
        return <ErrorS color="danger" />;
      case 'warning':
        return <WarningS color="warning" />;
      case 'info':
        return <InformationS color="info" />;
      case 'confirmation':
        return <SuccessS color="success" />;
      default:
        return null;
    }
  };

  const { direction } = useUser();
  const makeFocusRingVisible =
    showFocusRingOverride !== undefined ? showFocusRingOverride : showFocusRing;

  const {
    classes,
    styles: {
      tabBarItemIcon,
      tabBarItemIconStacked,
      tabBarItemMetadata,
      tabBarItemStackedBadge,
      tabBarItemStackedBadgeRtl,
      tabBarItemRemoveIcon
    }
  } = useComponentTheme<TabBarItemVariantOptions>(TabBarItemRedwoodTheme, {
    selection: isSelected ? 'isSelected' : 'notSelected',
    current: isCurrent ? 'isCurrent' : 'notCurrent',
    focusRing: makeFocusRingVisible ? 'showFocusRing' : 'noFocusRing',
    edgeBottom: isEdgeBottom ? 'isBottom' : 'notBottom'
  });

  const tabBarItem = classNames([classes]);
  const tabBarItemIconClasses = classNames([
    tabBarItemIcon,
    (isStacked || isIconOnly || !labelRequirement) && tabBarItemIconStacked
  ]);
  const tabBarItemMetadataClasses = classNames([tabBarItemMetadata]);
  const tabBarItemStackedBadgeClasses = classNames([
    tabBarItemStackedBadge,
    direction === 'rtl' && tabBarItemStackedBadgeRtl
  ]);
  const removeClass = classNames([tabBarItemRemoveIcon]);

  //instead of specifying flex: '1 0 auto' in cssProps var
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const flexDimensions = styleInterpolations({
    flex: layout === 'stretch' ? '1 0 auto' : '0 0 auto'
  });

  return (
    <div
      data-oj-key={itemKey}
      data-oj-removable={removeIcon ? 'true' : undefined}
      id={tabItemPrefix + itemKey}
      class={tabBarItem}
      ref={ref}
      role="tab"
      aria-selected={isSelected}
      {...pressProps}
      style={flexDimensions}>
      <Flex
        minWidth="10.5x"
        minHeight={isTall ? '14.5x' : '100%'}
        height={isStacked && labelRequirement ? '18.5x' : '10.5x'}
        gap="2x"
        flex="1 1 auto">
        <Flex
          direction={isStacked && labelRequirement ? 'column' : 'row'}
          gap="2x"
          align="center"
          justify="center"
          flex="1 1 auto">
          {(isStacked || isIconOnly || (!labelRequirement && icon)) && (
            <div class={tabBarItemIconClasses}>
              {icon}
              <span class={tabBarItemStackedBadgeClasses}>
                {badge ? (
                  <Badge size="xs" variant="neutral">
                    {badge}
                  </Badge>
                ) : null}
              </span>
            </div>
          )}
          {((!isIconOnly && labelRequirement) ||
            metadata ||
            severityRequirement ||
            removeIcon ||
            (icon && labelRequirement && !isStacked && !isIconOnly)) && (
            <Flex gap="2x" align="center" justify="center">
              {!isIconOnly && !isStacked && icon && labelRequirement && (
                <span class={tabBarItemIconClasses}>{icon}</span>
              )}
              {!isIconOnly && labelRequirement && (
                <Text size="md" weight="semiBold">
                  {label}
                </Text>
              )}
              {!isStacked && !isIconOnly && badge && labelRequirement && (
                <Badge size="md" variant="neutralSubtle">
                  {badge}
                </Badge>
              )}
              {metadata && (
                <span class={tabBarItemMetadataClasses}>
                  <Text>{'( ' + metadata + ' )'}</Text>
                </span>
              )}
              {severityRequirement && (
                <span class={tabBarItemIconClasses}>{IconComponent(severity)}</span>
              )}
            </Flex>
          )}
        </Flex>
        {removeIcon && <span class={removeClass}>{removeIcon}</span>}
      </Flex>
    </div>
  );
}
